"use client";

import {
  IconArrowDown,
  IconArrowSquareOut,
  IconClock,
  type IconComponent,
  IconLockSimple,
} from "@decibeltrade/react-icons";
import { Button } from "@decibeltrade/web-core/components";
import { useLocale, useTranslations } from "next-intl";
import { css, cva, cx } from "styled-system/css";

import { resolveCampaignDisplay } from "@/components/campaign-display";
import { Card } from "@/components/card/card";
import { NumberFormatter } from "@/utils/number-formatter";
import { getServerTime } from "@/utils/server-time";
import { shortHash } from "@/utils/string";
import { getExplorerUrl } from "@/utils/url";

import type { Reward } from "../../types";
import { RewardCardTags } from "./reward-card-tags";

interface RewardCardProps {
  reward: Reward;
  onClaim: (reward: Reward) => void;
  claimingCampaignId: number | null;
  anyClaimInFlight: boolean;
}

export function RewardCard({
  reward,
  onClaim,
  claimingCampaignId,
  anyClaimInFlight,
}: RewardCardProps) {
  const t = useTranslations("rewards.card");
  const tCampaigns = useTranslations("campaigns");
  const tCampaignsById = useTranslations("campaigns.byId");
  const tCampaignsByType = useTranslations("campaigns.byType");
  const isThisClaiming = claimingCampaignId === reward.id;
  const isAnyClaiming = anyClaimInFlight;

  const isClaimable = reward.status === "claimable";

  const { displayTitle, displayDescription } = resolveCampaignDisplay({
    campaignId: String(reward.id),
    campaignType: reward.campaignType,
    chainTitle: reward.title,
    chainDescription: reward.description,
    byIdLookup: (key) => (tCampaignsById.has(key) ? tCampaignsById(key) : null),
    byTypeLookup: (key) => (tCampaignsByType.has(key) ? tCampaignsByType(key) : null),
    fallbackTitle: tCampaigns("rewardsAvailableFallback"),
  });

  return (
    <Card background="default" className={cx(cardStyles, !isClaimable && nonClaimableGradient)}>
      <RewardCardTags category={reward.category} status={reward.status} />

      <div className={cx(titleStyles({ status: reward.status }), css({ mt: "12" }))}>
        {displayTitle}
      </div>

      {displayDescription && (
        <div title={displayDescription} className={descriptionStyles}>
          {displayDescription}
        </div>
      )}

      <div className={cx(rewardLabelStyles({ status: reward.status }), css({ mt: "16" }))}>
        {t("rewardLabel")}
      </div>

      <div className={cx(amountStyles({ status: reward.status }), css({ mt: "4" }))}>
        {NumberFormatter.usdc(reward.rewardAmountUsd)}
      </div>

      <div className={css({ flexGrow: 1, minH: "24" })} />

      <RewardCardFooter reward={reward} />

      <RewardCardAction
        reward={reward}
        onClaim={onClaim}
        isThisClaiming={isThisClaiming}
        isAnyClaiming={isAnyClaiming}
      />
    </Card>
  );
}

function RewardCardFooter({ reward }: { reward: Reward }) {
  const t = useTranslations("rewards.card");
  const locale = useLocale();

  switch (reward.status) {
    case "claimed":
      return <ClaimedFooter reward={reward} />;
    case "claimable":
      return <FooterText text={formatClosesIn(reward.claimEndTsSec, t, locale)} />;
    case "not_eligible":
      return <FooterText text={t("notEligibleReason")} />;
    case "expired":
      return <FooterText text={t("expiredReason")} />;
    case "in_progress":
      return null;
  }
}

function FooterText({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className={footerStyles}>{text}</div>;
}

function ClaimedFooter({ reward }: { reward: Reward & { status: "claimed" } }) {
  const t = useTranslations("rewards.card");
  const locale = useLocale();
  const date = new Date(reward.claimedAtTsSec * 1000).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const txHash = reward.claimTxHash;
  if (!txHash) {
    return <div className={footerStyles}>{t("claimedOn", { date })}</div>;
  }

  return (
    <div className={footerStyles}>
      {t.rich("claimedOnWithHash", {
        date,
        hash: () => (
          <a
            href={getExplorerUrl(txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className={hashLinkStyles}
          >
            {shortHash(txHash)}
          </a>
        ),
      })}
    </div>
  );
}

interface ActionProps {
  reward: Reward;
  onClaim: (reward: Reward) => void;
  isThisClaiming: boolean;
  isAnyClaiming: boolean;
}

function RewardCardAction({ reward, onClaim, isThisClaiming, isAnyClaiming }: ActionProps) {
  const t = useTranslations("rewards.card");

  switch (reward.status) {
    case "claimable":
      return (
        <Button
          variant="primary"
          size="md"
          disabled={isAnyClaiming}
          onClick={() => onClaim(reward)}
          TailIcon={IconArrowDown}
          className={actionButtonStyles}
        >
          {isThisClaiming
            ? t("claiming")
            : t("claim", { amount: NumberFormatter.usdc(reward.rewardAmountUsd) })}
        </Button>
      );
    case "expired":
      return <DisabledLabelButton TailIcon={IconClock}>{t("expiredButton")}</DisabledLabelButton>;
    case "not_eligible":
      return (
        <DisabledLabelButton TailIcon={IconLockSimple}>
          {t("notEligibleButton")}
        </DisabledLabelButton>
      );
    case "claimed": {
      const txHash = reward.claimTxHash;
      if (!txHash) {
        return (
          <DisabledLabelButton TailIcon={IconArrowSquareOut}>
            {t("viewOnExplorer")}
          </DisabledLabelButton>
        );
      }
      return (
        <Button
          variant="secondary"
          size="md"
          onClick={() => window.open(getExplorerUrl(txHash), "_blank", "noopener,noreferrer")}
          TailIcon={IconArrowSquareOut}
          className={actionButtonStyles}
        >
          {t("viewOnExplorer")}
        </Button>
      );
    }
    case "in_progress":
      return null;
  }
}

function DisabledLabelButton({
  children,
  TailIcon,
}: {
  children: React.ReactNode;
  TailIcon: IconComponent;
}) {
  return (
    <Button
      variant="secondary"
      size="md"
      disabled
      TailIcon={TailIcon}
      className={actionButtonStyles}
    >
      {children}
    </Button>
  );
}

function formatClosesIn(
  claimEndTsSec: number,
  t: ReturnType<typeof useTranslations<"rewards.card">>,
  locale: string,
): string | null {
  if (!claimEndTsSec) {
    return null;
  }
  const nowSec = Math.floor(getServerTime() / 1000);
  const remaining = claimEndTsSec - nowSec;
  if (remaining <= 0) {
    return null;
  }

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);

  let time: string;
  if (days > 0) {
    time = `${formatUnit(days, "day", locale)} ${formatUnit(hours, "hour", locale)}`;
  } else if (hours > 0) {
    time = `${formatUnit(hours, "hour", locale)} ${formatUnit(minutes, "minute", locale)}`;
  } else {
    time = formatUnit(minutes, "minute", locale);
  }
  return t("closesIn", { time });
}

function formatUnit(value: number, unit: "day" | "hour" | "minute", locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit,
    unitDisplay: "narrow",
  }).format(value);
}

const cardStyles = css({
  display: "flex",
  flexDirection: "column",
  h: "full",
  p: "16",
  rounded: "300!",
});

const descriptionStyles = css({
  mt: "2",
  textStyle: "highlight.md",
  color: "text.tertiary",
  truncate: true,
});

const nonClaimableGradient = css({
  bgGradient: "rewardCardNonClaimable",
});

const footerStyles = css({ textStyle: "highlight.md", color: "text.disabled" });

const hashLinkStyles = css({
  color: "text.primary",
  textDecoration: "underline",
  textUnderlineOffset: "2",
  _hover: { color: "text.primary", textDecoration: "none" },
});

const actionButtonStyles = css({ mt: "12", alignSelf: "flex-start" });

const amountStyles = cva({
  base: { textStyle: "subheading.xl", fontWeight: "light" },
  variants: {
    status: {
      claimable: { color: "text.positive" },
      in_progress: { color: "text.secondary" },
      claimed: { color: "text.disabled" },
      not_eligible: { color: "text.disabled" },
      expired: {
        color: "text.disabled",
        // CSS line-through is locked to mid-text. Figma's strike sits below center,
        // so draw it as a gradient and pin position.
        w: "fit",
        backgroundImage: "[linear-gradient(currentColor, currentColor)]",
        backgroundSize: "[100% 1.5px]",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "[0 58%]",
      },
    },
  },
});

const titleStyles = cva({
  base: { textStyle: "subheading.sm" },
  variants: {
    status: {
      claimable: { color: "text.primary" },
      in_progress: { color: "text.primary" },
      claimed: { color: "text.tertiary" },
      not_eligible: { color: "text.tertiary" },
      expired: { color: "text.tertiary" },
    },
  },
});

const rewardLabelStyles = cva({
  base: { textStyle: "body.sm", letterSpacing: "[1px]" },
  variants: {
    status: {
      claimable: { color: "text.secondary" },
      in_progress: { color: "text.secondary" },
      claimed: { color: "text.tertiary" },
      not_eligible: { color: "text.tertiary" },
      expired: { color: "text.tertiary" },
    },
  },
});
